import { useCallback, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { DataGrid } from '../../../DataGrid';

import { EmptyFiles } from '../EmptyFiles';

import { DeleteFilesDialog } from './DeleteFilesDialog';
import { FilePreviewDrawer } from './FilePreviewDrawer';
import { FilesGroupActions } from './FilesGroupActions';
import { MoveFilesDialog } from './MoveFilesDialog';
import { RenameFileDialog } from './RenameFileDialog';
import { useFilesActions } from './hooks/useFilesActions.hook';
import { getColumns } from './get-columns';
import type { FileCategory } from './types/file-category.type';
import type { FileItem } from './types/file-item.type';
import type { AccessGate } from '../../../Access/types/access-gate.type';

const EMPTY_SELECTION: GridRowSelectionModel = {
  type: 'include',
  ids: new Set(),
};

interface FilesConsultationProps {
  readonly allowedFileCategories: readonly FileCategory[];
  readonly files: readonly FileItem[];
  readonly isDeletingFiles?: boolean;
  readonly isLoading: boolean;
  readonly isMovingFiles?: boolean;
  readonly isRenamingFile?: boolean;
  readonly previewFileId?: Id | null;
  readonly selectedFileCategoryId: Id;
  readonly onDeleteFiles: (fileIds: readonly Id[]) => Promise<void>;
  readonly onMoveFiles: (
    params: Readonly<{
      fileIds: readonly Id[];
      destinationFileCategoryId: Id;
    }>,
  ) => Promise<void>;
  readonly onPreviewChange?: (fileId: Id | null) => void;
  readonly onRenameFile: (
    params: Readonly<{ fileId: Id; newOriginalName: string }>,
  ) => Promise<void>;
  readonly onUploadClick: () => void;
  readonly uploadGate?: AccessGate;
}

export const FilesConsultation = ({
  allowedFileCategories,
  files,
  isDeletingFiles = false,
  isLoading,
  isMovingFiles = false,
  isRenamingFile = false,
  previewFileId,
  selectedFileCategoryId,
  onDeleteFiles,
  onMoveFiles,
  onPreviewChange,
  onRenameFile,
  onUploadClick,
  uploadGate,
}: Readonly<FilesConsultationProps>) => {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(EMPTY_SELECTION);
  const [prevSelectedFileCategoryId, setPrevSelectedFileCategoryId] = useState(
    selectedFileCategoryId,
  );

  const {
    closeDeleteDialog,
    closeMoveDialog,
    closePreview,
    closeRenameDialog,
    confirmDelete,
    confirmMove,
    confirmRename,
    deleteError,
    deletePreviewedFile,
    destinationFileCategories,
    fileToRename,
    filesById,
    isDeleteDialogOpen,
    isMoveDialogOpen,
    movePreviewedFile,
    moveError,
    openDeleteDialog,
    openMoveDialog,
    openPreview,
    openRenameDialog,
    pendingDeleteFileIds,
    pendingMoveFileIds,
    previewFile,
    renameError,
    renamePreviewedFile,
    visibleFiles,
  } = useFilesActions({
    allowedFileCategories,
    files,
    previewFileId,
    selectedFileCategoryId,
    onDeleteFiles,
    onMoveFiles,
    onPreviewChange,
    onRenameFile,
  });

  if (selectedFileCategoryId !== prevSelectedFileCategoryId) {
    setPrevSelectedFileCategoryId(selectedFileCategoryId);
    setRowSelectionModel(EMPTY_SELECTION);
  }

  const selectedFileIds = useMemo(() => {
    if (rowSelectionModel.type === 'exclude') {
      return visibleFiles
        .map((file) => file._id)
        .filter((id) => !rowSelectionModel.ids.has(id));
    }
    return [...rowSelectionModel.ids] as Id[];
  }, [visibleFiles, rowSelectionModel]);

  const openBulkDeleteDialog = useCallback(() => {
    openDeleteDialog(selectedFileIds);
  }, [openDeleteDialog, selectedFileIds]);

  const openRowDeleteDialog = useCallback(
    (file: FileItem) => {
      openDeleteDialog([file._id]);
    },
    [openDeleteDialog],
  );

  const openBulkMoveDialog = useCallback(() => {
    openMoveDialog(selectedFileIds);
  }, [openMoveDialog, selectedFileIds]);

  const openRowMoveDialog = useCallback(
    (file: FileItem) => {
      openMoveDialog([file._id]);
    },
    [openMoveDialog],
  );

  const clearSelection = useCallback(() => {
    setRowSelectionModel(EMPTY_SELECTION);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    confirmDelete(clearSelection);
  }, [confirmDelete, clearSelection]);

  const handleConfirmMove = useCallback(
    (destinationFileCategoryId: Id) => {
      confirmMove(destinationFileCategoryId, clearSelection);
    },
    [confirmMove, clearSelection],
  );

  const columns = useMemo(
    () =>
      getColumns({
        canMove: destinationFileCategories.length > 0,
        filesById,
        onView: openPreview,
        onRename: openRenameDialog,
        onMove: openRowMoveDialog,
        onDelete: openRowDeleteDialog,
      }),
    [
      destinationFileCategories.length,
      filesById,
      openPreview,
      openRenameDialog,
      openRowMoveDialog,
      openRowDeleteDialog,
    ],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (visibleFiles.length === 0) {
    return <EmptyFiles onUploadClick={onUploadClick} uploadGate={uploadGate} />;
  }

  return (
    <>
      <DataGrid
        checkboxSelection
        columns={columns}
        onRowSelectionModelChange={setRowSelectionModel}
        rowSelectionModel={rowSelectionModel}
        rows={visibleFiles}
        slotProps={{
          toolbar: {
            groupActions: (
              <FilesGroupActions
                onDeleteClick={openBulkDeleteDialog}
                onMoveClick={openBulkMoveDialog}
                selectedCount={selectedFileIds.length}
              />
            ),
          },
        }}
      />
      <DeleteFilesDialog
        count={pendingDeleteFileIds.length}
        error={deleteError}
        isLoading={isDeletingFiles}
        isOpen={isDeleteDialogOpen}
        onCancel={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      <MoveFilesDialog
        categories={destinationFileCategories}
        count={pendingMoveFileIds.length}
        error={moveError}
        isLoading={isMovingFiles}
        isOpen={isMoveDialogOpen}
        onCancel={closeMoveDialog}
        onConfirm={handleConfirmMove}
      />
      <RenameFileDialog
        error={renameError}
        isLoading={isRenamingFile}
        isOpen={!!fileToRename}
        onCancel={closeRenameDialog}
        onConfirm={confirmRename}
        originalName={fileToRename?.originalName ?? EMPTY_STRING}
      />
      <FilePreviewDrawer
        allowedFileCategories={allowedFileCategories}
        file={previewFile}
        files={visibleFiles}
        filesById={filesById}
        isDeletingFile={isDeletingFiles}
        isMovingFile={isMovingFiles}
        isRenamingFile={isRenamingFile}
        onClose={closePreview}
        onDeleteFile={deletePreviewedFile}
        onMoveFile={movePreviewedFile}
        onNavigate={openPreview}
        onRenameFile={renamePreviewedFile}
        selectedFileCategoryId={selectedFileCategoryId}
      />
    </>
  );
};
